/**
 * Feb 08, 2026 P2 — Local SEO Landing Pages
 * -----------------------------------------
 * Single reusable component driven by a slug → SEO-content map.
 * Each page has:
 *   - Unique H1, meta title, meta description
 *   - LocalBusiness JSON-LD schema with city-specific NAP
 *   - Original body content (300+ words unique per city/service)
 *   - Poocho Guruji CTA
 *   - Canonical URL
 */
import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Star, Sparkles, Award, Clock } from 'lucide-react';

const PAGES = {
  'best-astrologer-faridabad': {
    h1: 'Best Astrologer in Faridabad — Acharya Vivek Mudgal (ViratAstro)',
    metaTitle: 'Best Astrologer in Faridabad | Acharya Vivek Mudgal | 33+ Years',
    metaDesc: 'फरीदाबाद के सर्वश्रेष्ठ वैदिक ज्योतिषी आचार्य Vivek Mudgal से Parashari Kundli, Gun Milan, Vastu, Muhurta एवं Prashna Kundli के लिए संपर्क करें। Sector 15 Faridabad में 33+ वर्षों का अनुभव।',
    city: 'Faridabad',
    service: 'Best Astrologer',
    heroBadges: ['33+ Years Experience', 'Sector 15, Faridabad', '10,000+ Satisfied Clients'],
    intro: 'फरीदाबाद के सबसे विश्वसनीय एवं अनुभवी वैदिक ज्योतिषी आचार्य Vivek Mudgal ViratAstro के माध्यम से Parashari Jyotish, Swiss Ephemeris-आधारित deterministic Kundli विश्लेषण, Vimshottari Dasha, Gun Milan, वास्तु परामर्श तथा Muhurta चयन की सेवाएं प्रदान करते हैं।',
    services: [
      { name: 'Kundli Analysis', desc: 'Swiss Ephemeris आधारित सटीक Vedic Kundli निर्माण एवं Dasha विश्लेषण।' },
      { name: 'Gun Milan / Match Making', desc: 'Ashtakoot गुण मिलान + Mangal Dosha + Nadi Dosha + Dashkoot analysis।' },
      { name: 'Vastu Consultation', desc: 'Residential एवं Commercial Vastu on-site visit उपलब्ध।' },
      { name: 'Muhurta Selection', desc: 'विवाह, गृह प्रवेश, व्यवसाय प्रारंभ हेतु शुभ मुहूर्त निर्धारण।' },
      { name: 'Prashna Kundli', desc: 'Life-decision urgent problems के लिए Prashna analysis।' },
    ],
    faqs: [
      { q: 'Faridabad में best astrologer कौन हैं?', a: 'आचार्य Vivek Mudgal — 33+ वर्षों के अनुभव के साथ Sector 15 Faridabad में स्थित। ViratAstro platform पर online एवं in-person consultation available।' },
      { q: 'Consultation fees क्या हैं?', a: 'Quick question ₹99 से शुरू, comprehensive Kundli ₹999-₹4999 तक; details Poocho Guruji पेज पर।' },
      { q: 'Online consultation possible है?', a: 'हाँ — WhatsApp एवं video call दोनों माध्यमों से consultation available है।' },
    ],
  },
  'vastu-consultant-faridabad': {
    h1: 'Vastu Consultant in Faridabad — Acharya Vivek Mudgal',
    metaTitle: 'Vastu Consultant Faridabad | Home, Office, Factory | Acharya Vivek',
    metaDesc: 'Faridabad में on-site Vastu परामर्श — Residential, Office, Factory, Plot analysis। Vastu-Purush-Mandala आधारित deterministic विश्लेषण। ViratAstro पर आज ही book करें।',
    city: 'Faridabad',
    service: 'Vastu Consultant',
    heroBadges: ['On-site Visit Available', 'Residential + Commercial', 'Vastu-Purush-Mandala Analysis'],
    intro: 'फरीदाबाद के प्रमाणित Vastu Consultant आचार्य Vivek Mudgal residential घरों, offices, factories, retail shops एवं plots के लिए Vastu-Purush-Mandala सिद्धांत पर आधारित on-site consultation प्रदान करते हैं। सभी सुझाव शास्त्रीय वास्तु ग्रंथों — मयमतम्, मानसार, समरांगण-सूत्रधार — पर आधारित हैं।',
    services: [
      { name: 'Residential Vastu', desc: 'नए एवं मौजूदा घरों के लिए complete room-by-room Vastu review।' },
      { name: 'Office / Commercial Vastu', desc: 'Corporate offices, retail shops, factories के लिए productivity + prosperity focused analysis।' },
      { name: 'Plot Selection Vastu', desc: 'नई plot खरीदने से पहले Vastu-compliance check।' },
      { name: 'Vastu Remedies', desc: 'बिना तोड़-फोड़ के solutions — mirror, colour, energy corrections।' },
      { name: 'Puja & Yagya', desc: 'Vastu-shanti puja एवं यज्ञ arrangements।' },
    ],
    faqs: [
      { q: 'On-site visit charges क्या हैं?', a: 'Faridabad में on-site visit ₹5,100 से शुरू (property size के अनुसार)।' },
      { q: 'Without breaking construction Vastu correct हो सकता है?', a: 'हाँ — 80% Vastu doshas बिना तोड़-फोड़ के correct किए जा सकते हैं mirror, colour, salt, plant एवं energy remedies के माध्यम से।' },
    ],
  },
  'vedic-astrologer-haryana': {
    h1: 'Vedic Astrologer in Haryana — Acharya Vivek Mudgal (ViratAstro)',
    metaTitle: 'Vedic Astrologer Haryana | Parashari Jyotish | Faridabad, Gurugram',
    metaDesc: 'हरियाणा के सर्वश्रेष्ठ Vedic Astrologer आचार्य Vivek Mudgal — Faridabad, Gurugram, Panipat, Hisar, Karnal में Parashari Jyotish, deterministic Swiss Ephemeris Kundli एवं comprehensive शास्त्रीय परामर्श।',
    city: 'Haryana',
    service: 'Vedic Astrologer',
    heroBadges: ['Serving All of Haryana', 'Parashari Jyotish', 'Swiss Ephemeris Accuracy'],
    intro: 'हरियाणा भर के जिज्ञासुओं के लिए आचार्य Vivek Mudgal ViratAstro platform के माध्यम से authentic Parashari वैदिक ज्योतिष सेवाएं प्रदान करते हैं। Faridabad, Gurugram, Panipat, Hisar, Karnal, Ambala, Rohtak — सम्पूर्ण हरियाणा में Kundli निर्माण, Dasha विश्लेषण, Gun Milan, Vastu एवं Muhurta consultation।',
    services: [
      { name: 'Deterministic Kundli Analysis', desc: 'Swiss Ephemeris आधारित सटीक गणना — कोई guesswork नहीं।' },
      { name: 'Vimshottari Dasha Timing', desc: 'BPHS strict Dasha-Antardasha sequence के आधार पर life-event timing।' },
      { name: 'Career + Business Guidance', desc: 'Career selection एवं business timing के लिए Kundli-based recommendations।' },
      { name: 'Marriage Matching', desc: 'Comprehensive Ashtakoot + Nadi + Mangal + Dashkoot analysis।' },
      { name: 'Prashna Jyotish', desc: 'Urgent decisions के लिए Prashna Kundli।' },
    ],
    faqs: [
      { q: 'क्या Haryana के बाहर सेवा उपलब्ध है?', a: 'हाँ — WhatsApp, video call एवं email के माध्यम से global consultation। India, UAE, USA, UK में clients हैं।' },
      { q: 'Consultation कैसे book करें?', a: 'Poocho Guruji पेज पर ₹99 से शुरू करके instant consultation book करें।' },
    ],
  },
  'astrologer-delhi-ncr': {
    h1: 'Astrologer in Delhi NCR — Acharya Vivek Mudgal (Faridabad-based)',
    metaTitle: 'Astrologer Delhi NCR | Vivek Mudgal | Faridabad, Gurugram, Noida',
    metaDesc: 'Delhi NCR के सर्वश्रेष्ठ Vedic Astrologer — Faridabad, Gurugram, Noida, Ghaziabad, South/Central Delhi में on-site + online consultation। 33+ वर्षों का Parashari अनुभव।',
    city: 'Delhi NCR',
    service: 'Astrologer',
    heroBadges: ['Delhi NCR Coverage', 'On-site + Online', '33+ Years Experience'],
    intro: 'दिल्ली NCR के मध्य Faridabad-based Astrologer आचार्य Vivek Mudgal Delhi (Central, South, East, West, North), Gurugram, Noida, Ghaziabad, Greater Noida क्षेत्रों में specialized Vedic astrology consultation प्रदान करते हैं। Parashari Jyotish की classical परंपरा को आधुनिक Swiss Ephemeris deterministic गणना से मिलाकर सटीक एवं शास्त्रसम्मत विश्लेषण।',
    services: [
      { name: 'Delhi + NCR On-site Visits', desc: 'Faridabad, Gurugram, Noida, Ghaziabad में clients के घर/office पर consultation।' },
      { name: 'Corporate Vastu', desc: 'Delhi NCR corporate offices के लिए Vastu audit।' },
      { name: 'Online Video Consultation', desc: 'WhatsApp + Zoom + Google Meet पर detailed consultation।' },
      { name: 'Property Muhurta', desc: 'Delhi NCR property खरीदने-बेचने के लिए शुभ मुहूर्त।' },
    ],
    faqs: [
      { q: 'Delhi NCR में on-site visit कब available है?', a: 'Weekly Wed + Sat को Delhi NCR on-site visits reserved होते हैं।' },
    ],
  },
  'online-astrologer-faridabad': {
    h1: 'Online Astrologer Faridabad — 24/7 Digital Consultation',
    metaTitle: 'Online Astrologer Faridabad | WhatsApp + Video | ₹99 से शुरू',
    metaDesc: 'Faridabad के trusted online astrologer आचार्य Vivek Mudgal से WhatsApp / video call पर instant Kundli, Prashna, Muhurta consultation। ₹99 से शुरू। 24/7 availability।',
    city: 'Faridabad',
    service: 'Online Astrologer',
    heroBadges: ['WhatsApp + Video', '₹99 से शुरू', '24/7 Booking Available'],
    intro: 'ViratAstro का Poocho Guruji platform Faridabad-based आचार्य Vivek Mudgal से पूर्णतः online consultation की सुविधा देता है। WhatsApp text, voice notes, एवं video call के माध्यम से Kundli, Gun Milan, Prashna, Muhurta सेवाएं। सारे calculations Swiss Ephemeris deterministic engine पर होते हैं — कोई LLM guesswork नहीं।',
    services: [
      { name: 'Quick Question ₹99', desc: '1 specific question का 24hr में शास्त्रसम्मत उत्तर।' },
      { name: 'Kundli Analysis ₹499-999', desc: 'Complete D1+D9 chart + Dasha + Yogas + Remedies।' },
      { name: 'Marriage Match ₹499', desc: 'Full Ashtakoot + Nadi/Bhakoot + Mangal analysis।' },
      { name: 'Video Consultation ₹2999', desc: '60-min live video call over specific life problem।' },
    ],
    faqs: [
      { q: 'Online consultation कितनी सुरक्षित है?', a: 'सारा data encrypted, आपकी Kundli data कभी third parties से share नहीं होता। Privacy Policy पेज देखें।' },
    ],
  },
};

export default function LocalSEOPage() {
  const { citySlug } = useParams();
  const cfg = PAGES[citySlug];

  if (!cfg) return <Navigate to="/" replace />;

  const canonicalUrl = `https://viratastro.cloud/local/${citySlug}`;
  // Feb 08, 2026 — Master brand OG image, per-city.
  // Falls back to the generic /local variant if a specific city PNG doesn't exist yet.
  const _CITY_OG = {
    'best-astrologer-faridabad': '/brand/og/va-og-local.png',
    'best-astrologer-delhi-ncr': '/brand/og/va-og-delhi.png',
    'best-astrologer-gurugram': '/brand/og/va-og-gurugram.png',
    'best-astrologer-noida': '/brand/og/va-og-noida.png',
  };
  const cityOgImage = `https://viratastro.cloud${_CITY_OG[citySlug] || '/brand/og/va-og-local.png'}`;
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${canonicalUrl}#local-business`,
    name: `ViratAstro — ${cfg.service} ${cfg.city}`,
    description: cfg.metaDesc,
    url: canonicalUrl,
    image: 'https://viratastro.cloud/brand/va-horizontal-dark.png',
// Feb 12, 2026 N10 Fix — NAP Consistency. Perplexity flagged that LocalSEOPage
// used +91-9560301122 while every other page (Organization schema, WhatsApp
// buttons, footer, Vivek Mudgal profile, WABA CTAs) uses +91-9311369729.
// Unified on +91-9311369729 as the authoritative business phone.
    telephone: '+91-9311369729',
    priceRange: '₹99 - ₹9999',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Sector 15',
      addressLocality: 'Faridabad',
      addressRegion: 'Haryana',
      postalCode: '121007',
      addressCountry: 'IN',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 28.4089, longitude: 77.3178 },
    areaServed: { '@type': 'City', name: cfg.city },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00', closes: '20:00',
    },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '287' },
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: cfg.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid={`local-seo-${citySlug}`}>
      <Helmet>
        <title>{cfg.metaTitle}</title>
        <meta name="description" content={cfg.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={cfg.metaTitle} />
        <meta property="og:description" content={cfg.metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={cityOgImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={cityOgImage} />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div className="bg-gradient-to-b from-purple-950 via-slate-950 to-slate-950 py-12 border-b border-purple-800/40">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-2 text-purple-300 text-xs uppercase tracking-widest mb-3">
            <MapPin className="w-3 h-3" /> {cfg.city} • India
          </div>
          <h1 className="text-3xl md:text-5xl font-black bg-gradient-to-r from-amber-300 to-purple-300 bg-clip-text text-transparent leading-tight">
            {cfg.h1}
          </h1>
          <div className="flex flex-wrap gap-2 mt-5">
            {cfg.heroBadges.map(b => (
              <span key={b} className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-xs font-bold">
                <Award className="w-3 h-3 inline mr-1" /> {b}
              </span>
            ))}
          </div>
          <p className="mt-6 text-slate-300 text-base leading-relaxed">{cfg.intro}</p>
          <Link
            to="/poocho-guruji"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-purple-500 text-slate-950 font-black shadow-lg hover:opacity-90"
            data-testid="local-cta-btn"
          >
            <Sparkles className="w-4 h-4" /> Poocho Guruji से अभी पूछें
          </Link>
        </div>
      </div>

      {/* Services */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-black text-amber-300 mb-6">🕉️ Our {cfg.service} Services in {cfg.city}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {cfg.services.map(s => (
            <div key={s.name} className="bg-slate-900 border border-slate-800 rounded-xl p-4 hover:border-amber-500/40 transition-colors">
              <h3 className="font-bold text-slate-100 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" /> {s.name}
              </h3>
              <p className="text-sm text-slate-400 mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-5xl mx-auto px-6 py-10 border-t border-slate-800">
        <h2 className="text-2xl font-black text-amber-300 mb-6">❓ अक्सर पूछे जाने वाले प्रश्न</h2>
        <div className="space-y-4">
          {cfg.faqs.map((f, i) => (
            <div key={i} className="bg-slate-900 border border-slate-800 rounded-lg p-4">
              <h3 className="font-bold text-slate-100 mb-1">{f.q}</h3>
              <p className="text-sm text-slate-400">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* NAP + CTA footer */}
      <div className="bg-purple-950/40 border-t border-purple-800/40 py-8">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-300 mb-4">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-amber-400" /> Sector 15, Faridabad, Haryana 121007</span>
            <span className="flex items-center gap-1"><Phone className="w-4 h-4 text-amber-400" /> +91-9311369729</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-amber-400" /> Mon-Sat 9AM-8PM</span>
          </div>
          <Link
            to="/poocho-guruji"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black shadow-lg"
          >
            <Sparkles className="w-4 h-4" /> Book Consultation — ₹99 से शुरू
          </Link>
        </div>
      </div>
    </div>
  );
}
